import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { personalPatch, type PersonalRequestOptions } from '../../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson, sanitizeForTerminal } from '../../../lib/output';
import { PERSONAL_RESUMES_BASE, type PersonalUpdatePublishedStatusDto } from '@wport/core';

/** 同 view.ts 慣例：檔案小而聚焦，各檔各自複製一份，不額外拉一個共用模組。 */
function requireEncId(encId: string): string {
	const trimmed = encId.trim();
	if (!trimmed) {
		throw new CliError('enc_id must not be empty', ExitCode.InvalidArgument);
	}
	return trimmed;
}

/**
 * `resumes publish` / `resumes unpublish` 共用（R6，design doc §5.2）：
 * `PATCH /resumes/{enc_id}/published-status` body `{target_status: boolean}`（spec B 已裁必填，
 * 非 toggle）→ `{is_published}`。可逆操作，不需 `--confirm`（同 campaigns publish/unpublish 慣例，
 * 有別於 resumes delete）。
 *
 * 輸出用伺服器回傳的 `is_published`，不直接回顯呼叫端要求的 `target_status`——避免伺服器實際
 * 狀態與請求不一致時（理論上不該發生，但別預設請求即結果）誤導使用者。
 */
export async function runResumesPublishTransition(ctx: ResolvedContext, encId: string, action: 'publish' | 'unpublish'): Promise<void> {
	const trimmed = requireEncId(encId);
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const targetStatus = action === 'publish';

	const { body } = await personalPatch(
		opts,
		`${PERSONAL_RESUMES_BASE}/${encodeURIComponent(trimmed)}/published-status`,
		{ target_status: targetStatus } satisfies PersonalUpdatePublishedStatusDto,
		{ idempotencyKey: randomUUID() }
	);
	const result = unwrapDataResponse<{ is_published: boolean }>(body);

	if (ctx.format === 'json') {
		printJson({ enc_id: trimmed, is_published: result.is_published });
		return;
	}
	const verb = action === 'publish' ? 'Published' : 'Unpublished';
	process.stdout.write(`${verb} resume: ${sanitizeForTerminal(trimmed)}\n`);
}

export function registerPersonalResumesPublish(parent: Command): void {
	parent
		.command('publish <enc_id>')
		.description('Publish a resume (make it visible to employers)')
		.action(async (encId: string, _flags: Record<string, never>, command: Command) => {
			await runResumesPublishTransition(resolveContext(command), encId, 'publish');
		});
}

export function registerPersonalResumesUnpublish(parent: Command): void {
	parent
		.command('unpublish <enc_id>')
		.description('Unpublish a resume (hide it from employers)')
		.action(async (encId: string, _flags: Record<string, never>, command: Command) => {
			await runResumesPublishTransition(resolveContext(command), encId, 'unpublish');
		});
}
